/**
 * Typed fetch client for the four Scratchpad API routes (Slice C seam).
 *
 * All request/response shapes are imported from `shared/types/api.ts` — the
 * single source of truth shared with the server (Slice A). This module never
 * defines its own copies of those shapes.
 *
 * Error model (consumed by Slice B's editor):
 * - A `409` on update is *not* a thrown error. It is part of the optimistic
 *   concurrency contract, so {@link updatePad} returns a discriminated result
 *   (`{ ok: true, ... } | { ok: false, conflict }`). The editor branches on it
 *   to show the inline conflict banner.
 * - `404` / `410` throw {@link PadNotFoundError} (the route layer collapses both
 *   into the "not found or expired" page; the distinct `status` is preserved for
 *   any caller that wants it).
 * - Every other non-2xx (400, 429, 5xx, malformed JSON, network failure) throws
 *   {@link ApiError}, carrying the parsed error envelope when available.
 */

import type {
  ConflictResponse,
  CreatePadResponse,
  ErrorResponse,
  GetPadResponse,
  UpdatePadRequest,
  UpdatePadResponse,
} from "@shared/types/api";

const JSON_HEADERS = { "Content-Type": "application/json" } as const;

/** Base error for any non-2xx response the editor/shell can surface to a toast. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown> | undefined;

  constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** Thrown for 404 (never existed) and 410 (expired). The shell collapses both. */
export class PadNotFoundError extends ApiError {
  constructor(status: 404 | 410, code: string, message: string) {
    super(status, code, message);
    this.name = "PadNotFoundError";
  }
}

/** Network-level failure (fetch rejected) — distinct so the editor can message it. */
export class NetworkError extends ApiError {
  constructor(message: string) {
    super(0, "network_error", message);
    this.name = "NetworkError";
  }
}

/** Result of an update: success carries the new state; conflict carries server state. */
export type UpdatePadResult =
  | { ok: true; pad: UpdatePadResponse }
  | { ok: false; conflict: ConflictResponse };

async function parseErrorEnvelope(
  response: Response,
): Promise<{ code: string; message: string; details?: Record<string, unknown> }> {
  try {
    const data = (await response.json()) as ErrorResponse;
    if (data && typeof data === "object" && "error" in data && data.error) {
      return {
        code: data.error.code,
        message: data.error.message,
        details: data.error.details,
      };
    }
  } catch {
    // fall through to a generic envelope below
  }
  return {
    code: "unknown",
    message: `Request failed with status ${String(response.status)}`,
  };
}

/** Map a non-OK response to the right typed error and throw it. */
async function throwForResponse(response: Response): Promise<never> {
  const envelope = await parseErrorEnvelope(response);
  if (response.status === 404 || response.status === 410) {
    throw new PadNotFoundError(response.status, envelope.code, envelope.message);
  }
  throw new ApiError(response.status, envelope.code, envelope.message, envelope.details);
}

async function doFetch(input: string, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (cause) {
    const message = cause instanceof Error ? cause.message : "Network request failed";
    throw new NetworkError(message);
  }
}

/**
 * POST /api/scratchpads — mint a new pad. Used by the `/` route to auto-create
 * an empty pad before redirecting to `/p/:slug`.
 */
export async function createPad(body = ""): Promise<CreatePadResponse> {
  const response = await doFetch("/api/scratchpads", {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify({ body }),
  });
  if (!response.ok) {
    await throwForResponse(response);
  }
  return (await response.json()) as CreatePadResponse;
}

/** GET /api/scratchpads/:slug — fetch a pad. Throws PadNotFoundError on 404/410. */
export async function getPad(slug: string): Promise<GetPadResponse> {
  const response = await doFetch(`/api/scratchpads/${encodeURIComponent(slug)}`, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    await throwForResponse(response);
  }
  return (await response.json()) as GetPadResponse;
}

/**
 * PUT /api/scratchpads/:slug — optimistic-concurrency update. Returns a
 * discriminated result so the caller handles `409` inline rather than catching.
 */
export async function updatePad(slug: string, payload: UpdatePadRequest): Promise<UpdatePadResult> {
  const response = await doFetch(`/api/scratchpads/${encodeURIComponent(slug)}`, {
    method: "PUT",
    headers: JSON_HEADERS,
    body: JSON.stringify(payload),
  });
  if (response.status === 409) {
    const conflict = (await response.json()) as ConflictResponse;
    return { ok: false, conflict };
  }
  if (!response.ok) {
    await throwForResponse(response);
  }
  return { ok: true, pad: (await response.json()) as UpdatePadResponse };
}

/** DELETE /api/scratchpads/:slug — 204 on success. Throws on 404/410/429/5xx. */
export async function deletePad(slug: string): Promise<void> {
  const response = await doFetch(`/api/scratchpads/${encodeURIComponent(slug)}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    await throwForResponse(response);
  }
}
